import { clearToken, get, post, setToken } from "@/api/client";

export type Role = "platform_admin" | "org_admin" | "manager" | "employee";

export interface SessionUser {
  id: string;
  tenant_id: string | null;
  email: string;
  full_name: string | null;
  role: Role;
  is_active: boolean;
  created_at: string;
  last_login: string | null;
}

export interface TokenResponse {
  token: string;
  user: SessionUser;
}

export async function fetchSetupStatus(signal?: AbortSignal): Promise<boolean> {
  const r = await get<{ needs_setup: boolean }>("/api/auth/setup-status", signal);
  if (!r.ok) throw new Error(r.err.message);
  return r.data.needs_setup;
}

export async function setupPlatformAdmin(
  email: string,
  password: string,
  fullName: string | null,
): Promise<TokenResponse> {
  const r = await post<TokenResponse>("/api/auth/setup", {
    email,
    password,
    full_name: fullName || null,
  });
  if (!r.ok) throw new Error(r.err.message);
  setToken(r.data.token);
  return r.data;
}

export async function login(
  email: string,
  password: string,
  tenantSlug?: string,
): Promise<TokenResponse> {
  const body: Record<string, unknown> = { email, password };
  if (tenantSlug) body.tenant_slug = tenantSlug;
  const r = await post<TokenResponse>("/api/auth/login", body);
  if (!r.ok) throw new Error(r.err.message);
  setToken(r.data.token);
  return r.data;
}

export async function fetchMe(signal?: AbortSignal): Promise<SessionUser> {
  const r = await get<SessionUser>("/api/auth/me", signal);
  if (!r.ok) {
    if (r.err.status === 401) clearToken();
    throw new Error(r.err.message);
  }
  return r.data;
}

/** Request a password-reset link. Always resolves (anti-enumeration). */
export async function requestPasswordReset(email: string, tenantSlug?: string): Promise<void> {
  const body: Record<string, unknown> = { email };
  if (tenantSlug) body.tenant_slug = tenantSlug;
  const r = await post<{ ok: boolean }>("/api/auth/forgot-password", body);
  if (!r.ok) throw new Error(r.err.message);
}

/** Set a new password from a reset/invite token; logs the user in on success. */
export async function resetPassword(token: string, newPassword: string): Promise<TokenResponse> {
  const r = await post<TokenResponse>("/api/auth/reset-password", { token, new_password: newPassword });
  if (!r.ok) throw new Error(r.err.message);
  setToken(r.data.token);
  return r.data;
}

/** Change the signed-in user's password (requires the current one). */
export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  const r = await post<{ ok: boolean }>("/api/auth/change-password", {
    current_password: currentPassword,
    new_password: newPassword,
  });
  if (!r.ok) throw new Error(r.err.message);
}

/** Self-service signup: creates a trial tenant + org-admin; an invite e-mail follows. */
export async function signup(orgName: string, adminEmail: string, adminFullName: string | null): Promise<void> {
  const r = await post<{ ok: boolean }>("/api/auth/signup", {
    org_name: orgName,
    admin_email: adminEmail,
    admin_full_name: adminFullName || null,
  });
  if (!r.ok) throw new Error(r.err.message);
}

/** Public demo auto-login — issues a token for the demo employee (no credentials). */
export async function demoLogin(): Promise<TokenResponse> {
  const r = await post<TokenResponse>("/api/auth/demo-login", {});
  if (!r.ok) throw new Error(r.err.message);
  setToken(r.data.token);
  return r.data;
}

export function logout(): void {
  clearToken();
}
